/* eslint-disable max-lines */
import { TrackingDataAccordionTitleFluVaccine as TrackingDataAccordionTitle } from '../../../common/AnalyticsContext/LinkTracking';

interface Props {
  faqRef: React.RefObject<HTMLDivElement>;
}

/**
 * This file simply contains the text content for the Flu Vaccine FAQ component
 */
export const FAQ_CONTENT = ({ faqRef }: Props) => [
  {
    question: TrackingDataAccordionTitle.WHO_SHOULD_GET_THE_FLU_VACCINE,
    answer: (
      <>
        <p>
          Yearly influenza (flu) vaccinations are recommended for all people aged 6 months and over.
          Anyone who wants to protect themselves against the flu can talk to their local pharmacy,
          general practice, or immunisation provider about getting immunised.
        </p>
        <p>
          The{' '}
          <a
            href="https://immunisationhandbook.health.gov.au/vaccine-preventable-diseases/influenza-flu"
            target="_blank"
            rel="noreferrer"
          >
            Australian Immunisation Handbook
          </a>{' '}
          includes more information about{' '}
          <a
            href="https://immunisationhandbook.health.gov.au/vaccine-preventable-diseases/influenza-flu#recommendations"
            target="_blank"
            rel="noreferrer"
          >
            specific groups
          </a>{' '}
          who should get immunised against the flu.
        </p>
        <p>People at highest risk of being hospitalised with flu are:</p>
        <ul>
          <li>Babies and children aged 6 months to under 5 years</li>
          <li>People aged 65 years and over</li>
          <li>Aboriginal and Torres Strait Islander people aged 6 months and over</li>
          <li>Pregnant women at any stage of pregnancy</li>
          <li>
            People with{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://www.health.gov.au/health-topics/flu-influenza#who-is-at-risk-from-flu"
            >
              long-term medical conditions
            </a>
          </li>
          <li>People who have weakened immune systems</li>
          <li>People who are obese</li>
          <li>People who smoke</li>
          <li>People who haven't been vaccinated against the flu</li>
        </ul>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.WHAT_IF_IVE_RECENTLY_HAD_A_COVID_VACCINATION,
    answer: (
      <>
        <p>
          ATAGI's{' '}
          <a
            href="https://www.health.gov.au/news/updated-atagi-advice-on-the-administration-of-seasonal-influenza-vaccines-in-2021-december-2021"
            target="_blank"
            rel="noreferrer"
          >
            latest advice
          </a>{' '}
          is that COVID-19 and flu vaccines can now be co-administered (given at the same time as
          one another), with an ideal time to vaccinate being the same day as receiving your
          COVID-19 booster. This replaces the original advice that recommended waiting 7 days
          between shots. It does not matter in what order you get the vaccines.
        </p>
        <p>What about the Novavax shot which combines COVID-19 and Flu vaccine into one shot?</p>
        <p>
          Please refer to the{' '}
          <a
            href="https://www.health.gov.au/news/atagi-statement-on-the-use-of-novavax-covid-19-vaccine-nuvaxovid"
            target="_blank"
            rel="noreferrer"
          >
            latest advice
          </a>{' '}
          on Novavax in Australia.
        </p>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.WHEN_SHOULD_GET_THE_FLU_VACCINE,
    answer: (
      <>
        <p>
          New season flu vaccines are expected to be available from April 2022. Timing may be
          different for your local area. Check the Healthengine directory to find out when your
          local pharmacy, GP or immunisation provider has vaccine availability and when you will be
          able to book in to have the vaccine.
        </p>
        <p>
          Your annual influenza vaccine should occur anytime from April onwards to be protected for
          the peak flu season, which is generally June to September. The highest level of protection
          occurs in the first 3 to 4 months following vaccination.{' '}
        </p>
        <p>
          However, it is never too late to vaccinate since influenza can circulate in the community
          all year round.{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.health.gov.au/health-topics/immunisation/vaccines/influenza-flu-vaccine#when-to-get-the-influenza-vaccine"
          >
            More information.
          </a>{' '}
        </p>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.AM_I_ELIGIBLE_FOR_FREE_FLU_VACCINE,
    ref: faqRef,
    answer: (
      <>
        <p>
          Under the National Immunisation Program (NIP), free flu vaccines are provided to the
          following groups who are at higher risk of complications from flu:
        </p>
        <ul>
          <li>Babies and children aged 6 months to under 5 years</li>
          <li>All Aboriginal and Torres Strait Islander people aged 6 months and over</li>
          <li>
            People aged 6 months and over with certain medical conditions that increase their chance
            of severe influenza and its complications
          </li>
          <li>Pregnant women at any stage of pregnancy</li>
          <li>People aged 65 years and over</li>
        </ul>
        <p>
          <b>Free Flu Vaccination Announcement Information and Links</b>
          <p>
            With a sharp increase in severe flu cases this season, some states and territories have
            announced free flu vaccines for all individuals aged 6 months and over, until June 30
            2022.
          </p>
          <p>
            For more information on your state’s free flu vaccination program, please visit the
            following links:
          </p>
          <ul>
            <li>
              <a href="https://www.healthywa.wa.gov.au/Articles/F_I/Flu-Influenza-vaccine">
                Western Australia (WA)
              </a>
            </li>
            <li>
              <a href="https://www.health.vic.gov.au/news/free-flu-vaccinations-for-all-victorians">
                Victoria (VIC)
              </a>
            </li>
            <li>
              <a href="https://www.health.nsw.gov.au/news/Pages/20220530_00.aspx">
                New South Wales (NSW)
              </a>
            </li>
            <li>
              <a href="https://www.qld.gov.au/health/conditions/immunisation/free-influenza-vaccinations-2022">
                Queensland (QLD)
              </a>
            </li>
            <li>
              <a href="https://www.sahealth.sa.gov.au/wps/wcm/connect/public+content/sa+health+internet/healthy+living/protecting+your+health/preventing+disease+and+infection/stop+the+flu+before+it+stops+you/free+flu+vaccine+for+all+south+australians">
                South Australia (SA)
              </a>
            </li>
          </ul>
        </p>
        <p>
          Speak to your pharmacy, GP or{' '}
          <a href="https://www.health.gov.au/health-topics/immunisation/vaccines/influenza-flu-vaccine#when-to-get-the-influenza-vaccine">
            contact your state or territory Department of Health
          </a>{' '}
          to find out more information for free flu vaccinations.
        </p>
        <p>
          If you are not eligible for a free flu vaccine, you can book and purchase the vaccine from
          your pharmacy, GP or another immunisation provider.
        </p>
        <p>
          Generally, the price range for a flu shot is between $15 - $30, as prices may vary from
          provider to provider.
        </p>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.DO_I_NEED_TO_GET_VACCINATED_AGAIN_IF_LAST_YEAR,
    answer: (
      <>
        <p>
          Yes. Every year the World Health Organization makes recommendations about the influenza
          virus strains to be included in the vaccine. This usually results in a changed vaccine
          every year to protect against the types of influenza viruses that are expected to be most
          common that year.
        </p>
        <p>
          While behaviours such as increased hand washing and social distancing helped limit the
          spread of flu viruses across 2020 and 2021, a{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.health.gov.au/news/updated-atagi-advice-on-the-administration-of-seasonal-influenza-vaccines-in-2021-december-2021"
          >
            resurgence of influenza activity
          </a>{' '}
          is expected as public health and social measures are increasingly relaxed, including the
          opening of international borders.
        </p>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.WHICH_FLU_VACCINES_ARE_APPROVED_FOR_USE_IN_AUSTRALIA,
    answer: (
      <>
        <p>
          Please visit the{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://immunisationhandbook.health.gov.au/vaccine-preventable-diseases/influenza-flu"
          >
            Australian Immunisation Handbook
          </a>{' '}
          for all influenza vaccines available in Australia.
        </p>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.WHAT_IS_THE_AUSTRALIAN_IMMUNISATION_REGISTER,
    answer: (
      <>
        <p>
          The Australian Immunisation Register records vaccines given to all people in Australia.
        </p>
        <p>
          Your pharmacy, GP or immunisation provider is required to report all flu vaccinations to
          the Register. This includes some personal information such as your name, date of birth,
          contact details, and your Medicare card number. Find out how this data is managed in the{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.health.gov.au/privacy-policy-australian-immunisation-register"
          >
            Privacy Policy for the Australian Immunisation Register
          </a>
          .
        </p>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.IS_THE_FLU_VACCINE_SAFE,
    answer: (
      <>
        <p>
          Vaccination is the safest way to protect yourself and others from the flu. The influenza
          vaccine has a great safety record.
        </p>
        <p>
          You may experience minor side effects following vaccination. Most reactions are mild and
          last no more than a couple of days and you will recover without any problems.
        </p>
        <p>
          Common side effects of influenza vaccines include:
          <ul>
            <li>Pain, redness, swelling or hardness where the needle went in</li>
            <li>Fever, tiredness, body aches</li>
          </ul>
        </p>
        <p>
          Any medicine, including influenza vaccines, can have potentially serious side effects,
          such as a severe allergic reaction. However, these reactions are extremely rare.
        </p>
        <p>
          Talk to your immunisation provider about possible side effects of the flu vaccines, or if
          you or your child have side effects that worry you.
        </p>
        <p>
          The Consumer Medicine Information available on the{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.ebs.tga.gov.au/ebs/picmi/picmirepository.nsf/PICMI?OpenForm&t=&q=influenza&r"
          >
            Therapeutic Goods Administration
          </a>{' '}
          website lists the ingredients and side effects of each vaccine.
        </p>
      </>
    ),
  },
  {
    question: TrackingDataAccordionTitle.CAN_I_GET_VACCINATED_IF_I_HAVE_EGG_ALLERGY,
    answer: (
      <>
        <p>
          Yes. As the flu vaccine only contains minute traces of egg protein, people with egg
          allergy, including a history of{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://immunisationhandbook.health.gov.au/technical-terms#anaphylaxis"
          >
            anaphylaxis
          </a>{' '}
          , can be safely vaccinated with flu vaccines. If you have an egg allergy please discuss
          this with your GP or immunisation provider.
        </p>
      </>
    ),
  },
];
